import { useCallback } from "react";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import SouthEastRoundedIcon from "@mui/icons-material/SouthEastRounded";
import AddRoundedIcon from "@mui/icons-material/Add";

import Button, { ButtonTypes } from "../../../../../components/Button";
import { MultipleLineChartComponent } from "../../../../../components/Charts";
import { FocusableTooltip } from "../../../../../components/FocusableTooltip";
import { SegmentedControl } from "../../../../../components/SegmentedControl";
import { useResponsive } from "../../../../../hooks/useResponsive";
import {
  formatSignedCurrency,
  getNumberFormat,
  toTone,
  toneColor,
} from "../../../../../utils";
import { BREAKPOINTS } from "../../../../../utils/constants";
import {
  isPricedValuation,
  returnMessageFor,
  returnPercentText,
  valuationAmountText,
  valuationMessageFor,
} from "../../../../../services/valuation";

import { InvestmentsHistoryPoint } from "../../../../../services/investments/types";
import {
  HistoryDataTable,
  HistoryPointDetails,
  HistoryTableColumn,
  UnavailablePointsNote,
} from "../../../historyPoint";
import { MetricCaption } from "../../../styles";
import { brokerCostsCaptionOf, presentedTotalOf } from "../../../utils";
import { HERO_CHART_HEIGHT } from "../../constants";
import {
  ChartArea,
  ChartControls,
  ChartEmpty,
  CurrentValue,
  GrowthChip,
  GrowthPercent,
  GrowthValue,
  Headline,
  HeroCard,
  PortfolioLabel,
  SubStat,
  SubStatColumn,
  SubStatLabel,
  SubStatValue,
  SubStats,
  TopBar,
} from "./styles";
import { InvestmentsHeroProps } from "./types";

const TABLE_COLUMNS: HistoryTableColumn<InvestmentsHistoryPoint>[] = [
  { header: "Invested", valueOf: (point) => point.invested },
];

export const InvestmentsHero: React.FC<InvestmentsHeroProps> = ({
  periodLabel,
  totalValue,
  portfolioValue,
  valuation,
  investedValue,
  brokerCosts,
  accountFeeCosts,
  interestCosts,
  adjustmentsNet,
  changeValue,
  changePercent,
  changePercentStateReason,
  gainBasis,
  investmentCount,
  chartSeries,
  chartXLabels,
  chartPoints,
  chartWindow,
  isChartLoading,
  chartWindowOptions,
  onChartWindowChange,
  onCreateInvestment,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);
  const presentedValue = presentedTotalOf({ portfolioValue, totalValue });

  const tone = toTone(changeValue ?? 0);
  // One message behind the header, the placeholder and the accessible name, so
  // the hero cannot describe the same state three different ways — which is what
  // a hardcoded "Portfolio value unavailable" had already started doing.
  const valuationMessage = valuationMessageFor(valuation);
  // The same rule `valuationAmountText` applies, read from its one definition,
  // so the header and the accessible name can never claim a total the headline
  // is not showing.
  const isValued = isPricedValuation({ value: presentedValue, valuation });
  const headerLabel = isValued
    ? `Portfolio · ${periodLabel}`
    : `${valuationMessage} · ${periodLabel}`;
  const valueText = valuationAmountText({
    value: presentedValue,
    valuation,
    format: (value) => `${getNumberFormat(value)} €`,
  });
  // The nullness test is redundant with `isValued` and kept only because a
  // helper returning a plain boolean cannot narrow the type the way an inline
  // comparison did.
  const valueAriaLabel =
    isValued && presentedValue !== null
      ? `${getNumberFormat(presentedValue)} euros`
      : valuationMessage;
  // A trend needs at least two points; a single dot reads as a broken chart.
  const hasTrend = chartXLabels.length >= 2;
  const chartHeight = isCompact
    ? HERO_CHART_HEIGHT.compact
    : HERO_CHART_HEIGHT.regular;

  // Arrow direction tracks the tone so a loss never points up.
  let TrendIcon: typeof ArrowOutwardRoundedIcon | null = null;
  if (tone === "positive") {
    TrendIcon = ArrowOutwardRoundedIcon;
  } else if (tone === "negative") {
    TrendIcon = SouthEastRoundedIcon;
  }

  // Known even when the valuation is not: charges are recorded amounts, not
  // quotes, so this line stays truthful on a period the headline cannot value.
  const brokerCostsText = `${getNumberFormat(brokerCosts)} €`;
  // "of which" decomposes the figure above it rather than reading as a term to
  // add to Invested.
  const brokerCostsDetail = brokerCostsCaptionOf(
    brokerCosts,
    accountFeeCosts,
    (value) => `${getNumberFormat(value)} €`,
  );
  // The only signed sub-stat, so it carries the tone the growth chip does: a
  // credited residual and a debited one must not read alike but for a minus sign.
  const adjustmentsNetColor = toneColor(toTone(adjustmentsNet));

  const createButton = isCompact ? null : (
    <Button
      type={ButtonTypes.primary}
      size="medium"
      onClick={onCreateInvestment}
      aria-label="Create investment"
    >
      <AddRoundedIcon fontSize="small" />
      Create investment
    </Button>
  );

  const returnMessage =
    changePercent === null ? returnMessageFor(changePercentStateReason) : null;
  const growthNote = [returnMessage, gainBasis]
    .filter((note) => note !== null)
    .join(". ");
  let growthChip: React.ReactNode = null;
  // A withheld percentage never hides a real gain, so the chip gates on the euro figure alone.
  if (changeValue !== null) {
    const growthValueText = formatSignedCurrency(changeValue);
    const growthPercentText = returnPercentText({ value: changePercent });
    const plainChip = (
      <GrowthChip $tone={tone}>
        {TrendIcon ? <TrendIcon /> : null}
        <GrowthValue>{growthValueText}</GrowthValue>
        <GrowthPercent>{growthPercentText}</GrowthPercent>
      </GrowthChip>
    );
    growthChip = growthNote ? (
      <FocusableTooltip title={growthNote}>{plainChip}</FocusableTooltip>
    ) : (
      plainChip
    );
  }

  const chartRangeLabel =
    chartWindow === null
      ? periodLabel
      : (chartWindowOptions.find((option) => option.value === chartWindow)
          ?.label ?? chartWindow);
  const tooltipFooter = useCallback(
    (dataIndex: number) =>
      chartPoints[dataIndex] ? (
        <HistoryPointDetails point={chartPoints[dataIndex]} />
      ) : null,
    [chartPoints],
  );

  let chartContent: React.ReactNode;
  if (isChartLoading) {
    chartContent = <ChartEmpty aria-busy="true">Loading chart…</ChartEmpty>;
  } else if (hasTrend) {
    chartContent = (
      <>
        <div aria-hidden="true">
          <MultipleLineChartComponent
            series={chartSeries}
            xLabels={chartXLabels}
            height={chartHeight}
            hideYAxis
            hideXAxisLine
            hideLegend={isCompact}
            tooltipFooter={tooltipFooter}
          />
        </div>
        <HistoryDataTable
          caption={`Portfolio value, ${chartRangeLabel}`}
          points={chartPoints}
          columns={TABLE_COLUMNS}
        />
      </>
    );
  } else {
    chartContent = (
      <ChartEmpty>Not enough history to chart this period yet.</ChartEmpty>
    );
  }

  return (
    <HeroCard>
      <TopBar>
        <PortfolioLabel>{headerLabel}</PortfolioLabel>
        {createButton}
      </TopBar>

      <Headline>
        <CurrentValue aria-label={valueAriaLabel}>{valueText}</CurrentValue>
        {growthChip}
      </Headline>

      <ChartArea>
        {chartWindow !== null && (
          <ChartControls>
            <SegmentedControl
              options={chartWindowOptions}
              value={chartWindow}
              onChange={onChartWindowChange}
              ariaLabel="Chart range"
              testId="investments-chart-range"
            />
          </ChartControls>
        )}
        {chartContent}
        {/* No repair here: this page has no override panel to open. */}
        {!isChartLoading && <UnavailablePointsNote points={chartPoints} />}
      </ChartArea>

      <SubStats>
        <SubStat>
          <SubStatLabel>Invested</SubStatLabel>
          <SubStatValue>{getNumberFormat(investedValue)} €</SubStatValue>
        </SubStat>
        <SubStat>
          <SubStatLabel>Broker costs</SubStatLabel>
          <SubStatColumn>
            <SubStatValue>{brokerCostsText}</SubStatValue>
            {brokerCostsDetail && (
              <MetricCaption>{brokerCostsDetail}</MetricCaption>
            )}
          </SubStatColumn>
        </SubStat>
        {interestCosts > 0 && (
          <SubStat>
            <SubStatLabel>Interest charged</SubStatLabel>
            <SubStatValue>{getNumberFormat(interestCosts)} €</SubStatValue>
          </SubStat>
        )}
        {adjustmentsNet !== 0 && (
          <SubStat>
            <SubStatLabel>Adjustments</SubStatLabel>
            <SubStatValue $color={adjustmentsNetColor}>
              {getNumberFormat(adjustmentsNet)} €
            </SubStatValue>
          </SubStat>
        )}
        <SubStat>
          <SubStatLabel>Investments</SubStatLabel>
          <SubStatValue>{investmentCount}</SubStatValue>
        </SubStat>
      </SubStats>
    </HeroCard>
  );
};
